/**
 * Smart Shikka AI Core — PR92
 * No-Runtime Foundation Handoff — Integration Readiness
 * Registry — Deep Frozen — No Execution
 */
function deepFreeze(obj) {
    if (obj && typeof obj === "object") {
        Object.freeze(obj);
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (val && typeof val === "object" && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    "approval_execute", "child_process", "message_send",
    "old_assistant_alias", "provider_call", "runtime_action_execute",
    "runtime_unlock", "secret_literal", "shell_command",
    "tool_execution", "unapproved_db_write", "unapproved_file_write",
    "unapproved_network_call", "unknown_provider_call", "unknown_tool_execution",
    "unredacted_secret", "unsafe_env_dump",
    "action_execute", "agent_execution", "workflow_execution",
    "gate_unlock", "file_read", "file_write",
    "consent_persist", "role_mutation", "permission_mutation",
    "state_persist", "local_storage_write", "session_storage_write",
    "indexed_db_write", "sqlite_write", "db_write",
    "supabase_call", "chat_runtime_start", "model_inference",
    "render_document", "inject_icon_runtime", "browser_action",
    "desktop_action", "run_pake_command", "network_call",
    "secret_read", "attachment_parse", "upload_file", "user_drive_access",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    handoffExecutionAllowedInPr92: false,
    runtimeActionAllowedInPr92: false,
    runtimeUnlockAllowedInPr92: false,
    providerCallAllowedInPr92: false,
    toolExecutionAllowedInPr92: false,
    agentExecutionAllowedInPr92: false,
    workflowExecutionAllowedInPr92: false,
    messageSendAllowedInPr92: false,
    chatRuntimeAllowedInPr92: false,
    documentRenderAllowedInPr92: false,
    iconInjectionAllowedInPr92: false,
    browserAutomationAllowedInPr92: false,
    desktopAutomationAllowedInPr92: false,
    pakeExecutionAllowedInPr92: false,
    smartPrintDriveAccessAllowedInPr92: false,
    fileMutationAllowedInPr92: false,
    networkAllowedInPr92: false,
    dbWriteAllowedInPr92: false,
    localStorageAllowedInPr92: false,
    sessionStorageAllowedInPr92: false,
    indexedDbAllowedInPr92: false,
    sqliteAllowedInPr92: false,
    supabaseAllowedInPr92: false,
    secretAccessAllowedInPr92: false,
    approvalExecutionAllowedInPr92: false,
    approvalPersistenceAllowedInPr92: false,
    roleMutationAllowedInPr92: false,
    permissionMutationAllowedInPr92: false,
    modelInferenceAllowedInPr92: false,
    attachmentParsingAllowedInPr92: false,
    gateUnlockAllowedInPr92: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "foundation_complete_handoff_summary",
        displayName: "Foundation Complete Handoff Summary",
        description: "Hard close of the no-runtime foundation. All PR84-PR91 layers verified, chain connected, gates locked.",
        sourceModules: [
            "smart-shikka-assistant-orchestrator",
            "smart-shikka-assistant-ui-route-preview",
            "smart-shikka-assistant-readonly-chat-preview",
            "smart-shikka-assistant-readonly-conversation-state-preview",
            "smart-shikka-assistant-readonly-approval-state-preview",
            "smart-shikka-assistant-readonly-action-request-preview",
            "smart-shikka-assistant-readonly-execution-gate-matrix-preview",
            "smart-shikka-assistant-no-runtime-safety-audit-pack",
        ],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: ["document_render_request", "workflow_plan_request", "icon_identity_request", "capability_discovery_request", "safety_guardrail_explain_request", "desktop_shell_plan_request", "smart_print_local_data_plan_request", "source_adoption_audit_request", "audit_report_request", "future_agent_task_request"],
        relatedUiPreviewIds: ["assistant_home_panel", "route_picker_panel"],
        relatedChatPreviewIds: ["readonly_conversation_shell", "readonly_message_thread_preview", "blocked_message_send_card", "provider_call_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot", "readonly_owner_approval_state", "readonly_user_approval_state"],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state", "readonly_owner_approval_required_state", "readonly_dual_approval_required_state"],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary", "readonly_action_request_from_route"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Foundation handoff is metadata only", "No runtime action in PR92", "All gates remain locked"],
        futurePrScope: ["PR93+ live MVP scope"],
        notes: ["Hard close of no-runtime foundation phase"],
    },
    {
        id: "route_layer_ready",
        displayName: "Route Layer Ready",
        description: "PR84 orchestrator routes verified and ready for live-phase wiring.",
        sourceModules: ["smart-shikka-assistant-orchestrator"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: ["document_render_request", "workflow_plan_request", "icon_identity_request", "capability_discovery_request", "safety_guardrail_explain_request", "desktop_shell_plan_request", "smart_print_local_data_plan_request", "source_adoption_audit_request", "audit_report_request", "future_agent_task_request"],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Route layer is preview-only metadata", "No live routing in PR92"],
        futurePrScope: ["Live route wiring in PR93+"],
        notes: ["10 route entries from PR84 verified"],
    },
    {
        id: "ui_route_layer_ready",
        displayName: "UI Route Layer Ready",
        description: "PR85 UI route previews verified and ready for live-phase rendering.",
        sourceModules: ["smart-shikka-assistant-ui-route-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: ["assistant_home_panel", "route_picker_panel", "document_render_route_card", "workflow_plan_route_card", "icon_identity_route_card", "capability_discovery_route_card", "safety_guardrail_explain_card", "desktop_shell_plan_route_card", "smart_print_local_data_plan_card", "source_adoption_audit_card", "audit_report_route_card", "future_agent_task_blocked_card"],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["UI route layer is preview-only", "No live rendering in PR92"],
        futurePrScope: ["Live UI rendering in PR93+"],
        notes: ["12 UI preview entries from PR85 verified"],
    },
    {
        id: "readonly_chat_layer_ready",
        displayName: "Readonly Chat Layer Ready",
        description: "PR86 readonly chat contract previews verified. No message send, no provider call.",
        sourceModules: ["smart-shikka-assistant-readonly-chat-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["readonly_conversation_shell", "readonly_message_thread_preview", "user_message_draft_preview", "assistant_response_placeholder", "route_context_badge_panel", "safety_warning_panel", "blocked_message_send_card", "provider_call_blocked_card", "tool_execution_blocked_card", "attachment_placeholder_readonly_card", "audit_transcript_preview", "future_chat_runtime_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Chat layer is readonly", "No message send in PR92", "No provider call in PR92"],
        futurePrScope: ["Live chat runtime in PR93+"],
        notes: ["12 chat preview entries from PR86 verified"],
    },
    {
        id: "conversation_state_layer_ready",
        displayName: "Conversation State Layer Ready",
        description: "PR87 readonly conversation state previews verified. No persistence.",
        sourceModules: ["smart-shikka-assistant-readonly-conversation-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot", "readonly_selected_route_state", "readonly_message_draft_state", "readonly_message_thread_state", "readonly_response_placeholder_state", "readonly_route_context_state", "readonly_safety_warning_state", "readonly_blocked_runtime_state", "readonly_attachment_placeholder_state", "readonly_audit_transcript_state", "readonly_ui_selection_state", "readonly_owner_approval_state", "readonly_user_approval_state", "future_persistent_conversation_state_blocked"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Conversation state is readonly", "No persistence in PR92"],
        futurePrScope: ["Live state persistence in PR93+"],
        notes: ["14 conversation state preview entries from PR87 verified"],
    },
    {
        id: "approval_state_layer_ready",
        displayName: "Approval State Layer Ready",
        description: "PR88 readonly approval state previews verified. No role mutation, no persistence.",
        sourceModules: ["smart-shikka-assistant-readonly-approval-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state", "readonly_owner_approval_required_state", "readonly_user_approval_required_state", "readonly_dual_approval_required_state", "readonly_approval_denied_state", "readonly_approval_pending_state", "readonly_blocked_role_mutation_state", "readonly_blocked_permission_mutation_state", "readonly_blocked_runtime_unlock_state", "readonly_provider_call_approval_blocked_state", "readonly_tool_execution_approval_blocked_state", "readonly_file_access_approval_blocked_state", "readonly_persistence_approval_blocked_state", "readonly_approval_audit_evidence_placeholder", "readonly_approval_policy_explain_state", "future_approval_runtime_blocked_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Approval state is readonly", "No role mutation in PR92", "No persistence in PR92"],
        futurePrScope: ["Live approval runtime in PR93+"],
        notes: ["16 approval state preview entries from PR88 verified"],
    },
    {
        id: "action_request_layer_ready",
        displayName: "Action Request Layer Ready",
        description: "PR89 readonly action request previews verified. No execution, no persistence.",
        sourceModules: ["smart-shikka-assistant-readonly-action-request-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary", "readonly_action_request_from_route", "readonly_action_request_from_chat_draft", "readonly_action_request_requires_owner_approval", "readonly_action_request_requires_user_approval", "readonly_action_request_requires_dual_approval", "readonly_provider_action_request_blocked", "readonly_tool_action_request_blocked", "readonly_agent_action_request_blocked", "readonly_workflow_action_request_blocked", "readonly_document_render_action_request_blocked", "readonly_icon_injection_action_request_blocked", "readonly_file_access_action_request_blocked", "readonly_browser_action_request_blocked", "readonly_desktop_action_request_blocked", "readonly_smart_print_local_data_action_request_blocked", "readonly_audit_action_request_placeholder", "future_action_execution_runtime_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Action requests are readonly", "No execution in PR92"],
        futurePrScope: ["Live action execution in PR93+"],
        notes: ["18 action request preview entries from PR89 verified"],
    },
    {
        id: "execution_gate_layer_ready",
        displayName: "Execution Gate Layer Ready",
        description: "PR90 readonly execution gate matrix verified. No gate unlock, no execution.",
        sourceModules: ["smart-shikka-assistant-readonly-execution-gate-matrix-preview"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Execution gates are locked", "No gate unlock in PR92"],
        futurePrScope: ["Per-gate unlock in PR93+"],
        notes: ["16 execution gate preview entries from PR90 verified"],
    },
    {
        id: "audit_pack_ready",
        displayName: "Audit Pack Ready",
        description: "PR91 end-to-end safety audit pack verified. Chain connected, all audits pass.",
        sourceModules: ["smart-shikka-assistant-no-runtime-safety-audit-pack"],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit pack is metadata only", "No runtime action in PR92"],
        futurePrScope: ["Live audit execution in PR93+"],
        notes: ["12 audit entries from PR91 verified"],
    },
    {
        id: "live_phase_entry_criteria",
        displayName: "Live Phase Entry Criteria",
        description: "Defines criteria that must be met before any live-phase PR can unlock a gate.",
        sourceModules: [
            "smart-shikka-assistant-orchestrator",
            "smart-shikka-assistant-readonly-execution-gate-matrix-preview",
            "smart-shikka-assistant-no-runtime-safety-audit-pack",
        ],
        targetPreviewModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Entry criteria definition is metadata only", "No live action in PR92"],
        futurePrScope: ["Live-phase gate unlock requires meeting these criteria"],
        notes: [
            "Criteria: owner approval + user approval per gate",
            "Criteria: audit trail entry per unlock",
            "Criteria: specific gate ID scope",
            "Criteria: no batch unlock",
        ],
    },
    {
        id: "hard_cutoff_policy",
        displayName: "Hard Cutoff Policy",
        description: "After PR92, no more foundation-preview PRs unless fixing bugs. Hard boundary for no-runtime phase.",
        sourceModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Hard cutoff is policy metadata", "No new foundation PRs after PR92"],
        futurePrScope: ["Bug fixes only for foundation layer"],
        notes: ["No more foundation-preview PRs after PR92", "Next PRs are live-phase only"],
    },
    {
        id: "future_live_mvp_scope",
        displayName: "Future Live MVP Scope",
        description: "Placeholder defining what the live MVP phase will cover. No action taken in PR92.",
        sourceModules: ["smart-shikka-assistant-no-runtime-foundation-handoff"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Future scope is placeholder metadata", "No live action in PR92"],
        futurePrScope: [
            "Document render (low risk)",
            "Workflow planning (medium risk)",
            "Message send (medium risk)",
            "Provider call (high risk)",
            "Tool execution (high risk)",
            "File access (high risk)",
            "Browser/Desktop automation (highest risk)",
        ],
        notes: ["Live MVP scope defined but not executed", "Each gate unlock requires separate PR with dual approval"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantNoRuntimeFoundationHandoffEntries() {
    return registry;
}
export function getSmartShikkaAssistantNoRuntimeFoundationHandoffEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantNoRuntimeFoundationHandoffRegistry.js.map